import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  ReplaySubject,
  Subject,
  debounceTime,
  finalize,
  startWith,
  takeUntil,
} from "rxjs";
import { UserData } from "src/app/_models/user.model";
import { AdminService } from "src/app/_services/admins.service";
import { LoadingTypeEnum } from "../../shared/_enums/loading-type.enum";

interface AccountManagerOption {
  id: number;
  label: string;
}

export interface CompanyAccountManagersDialogData {
  excludeIds: number[];
}

@Component({
  selector: "app-company-account-managers-dialog",
  templateUrl: "./company-account-managers-dialog.component.html",
  styleUrls: ["./company-account-managers-dialog.component.scss"],
})
export class CompanyAccountManagersDialogComponent
  implements OnInit, OnDestroy
{
  public isLoading = false;
  public form: FormGroup;
  public adminOptions: AccountManagerOption[] = [];
  public adminsMultiFilterCtrl = new FormControl("");
  public filteredAdminOptions = new ReplaySubject<AccountManagerOption[]>(1);

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: CompanyAccountManagersDialogData,
    private readonly dialogRef: MatDialogRef<CompanyAccountManagersDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly adminService: AdminService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      account_manager_ids: [[], Validators.required],
    });

    this.adminsMultiFilterCtrl.valueChanges
      .pipe(startWith(""), debounceTime(200), takeUntil(this.destroy$))
      .subscribe((search) => {
        this.filterAdminOptions(search);
      });

    this.loadAdmins();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.dialogRef.close(
      this.normalizeIds(this.form.get("account_manager_ids")?.value)
    );
  }

  public removeSelectedAdmin(adminId: number): void {
    const selectedAdminIds = this.normalizeIds(
      this.form.get("account_manager_ids")?.value
    ).filter((id) => id !== adminId);

    this.form.get("account_manager_ids")?.setValue(selectedAdminIds);
  }

  public getSelectedAdminLabel(adminId: number): string {
    return (
      this.adminOptions.find((option) => option.id === adminId)?.label ||
      `#${adminId}`
    );
  }

  private loadAdmins(): void {
    this.isLoading = true;

    this.adminService
      .getAdmins({
        per_page: 1000,
        order: "first_name",
        order_direction: "asc",
        status_id: 3,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response) => {
          const excludeIds = new Set(this.normalizeIds(this.data?.excludeIds));
          const adminItems = response?.result?.items ?? [];

          this.adminOptions = adminItems
            .filter((admin: UserData) => !excludeIds.has(admin.id))
            .map((admin: UserData) => ({
              id: admin.id,
              label: this.getAdminLabel(admin),
            }))
            .sort((left, right) => left.label.localeCompare(right.label));

          this.filterAdminOptions(this.adminsMultiFilterCtrl.value);
        },
        error: () => {
          this.adminOptions = [];
          this.filterAdminOptions(this.adminsMultiFilterCtrl.value);
        },
      });
  }

  private getAdminLabel(admin: Partial<UserData>): string {
    const name =
      admin.name?.trim() ||
      [admin.first_name, admin.last_name].filter(Boolean).join(" ").trim();

    if (name && admin.email) {
      return `${name} (${admin.email})`;
    }

    return name || admin.email || `#${admin.id}`;
  }

  private normalizeIds(ids: number[] | null | undefined): number[] {
    return Array.from(
      new Set(
        [...(ids ?? [])]
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      )
    ).sort((left, right) => left - right);
  }

  private filterAdminOptions(search: string | null): void {
    if (!search) {
      this.filteredAdminOptions.next(this.adminOptions.slice());
      return;
    }

    const normalizedSearch = search.toLowerCase();
    this.filteredAdminOptions.next(
      this.adminOptions.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearch)
      )
    );
  }

  get LoadingType() {
    return LoadingTypeEnum;
  }
}
